import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import {useTranslation} from 'react-i18next';
import Modal from '../../components/Modal';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import {
	callAlertWeb,
	callNativeWeb,
	clickSecurityKeypadEventWeb,
	footerShowEventWeb
} from 'common/appInterface';
import { useLoading } from 'components/Loading/LoadingProvider';

const PasswordChangePage: React.FC = () => {
	/** Location State */
	const location = useLocation() as Location<LocationState>;
	/** Navigate */
	const navigate = useNavigate();
	/** 다국어처리 */
	const {t, i18n} = useTranslation();

  const { showLoading, hideLoading } = useLoading();
	
	const screenId = "SERV02";
	const screenName = t("SERV02.title");
	
	const [isModal, setModal] = useState(false);
	const cardInfo = location.state.info;
	const [cvc, setCvc] = useState("");
	const [password, setPassword] = useState("");
	const [passwordConfirm, setPasswordConfirm] = useState("");
	const [passwordChange, setPasswordChange] = useState<Partial<PasswordChange>>({});
	const [passwordCheckCode, setPasswordCheckCode] = useState("");
	
	const cvcRef = useRef<HTMLParagraphElement>(null);
	const passwordRef = useRef<HTMLParagraphElement>(null);
	const passwordConfirmRef = useRef<HTMLParagraphElement>(null);
	
	/** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
	const callbackBackkeyEvent = useCallback(() => {
		if (location.state.linkOpenMenu === 'main') {
			navigate("/main", {
				state: {
					query: location.state.query
					, swiperIndex: location.state.swiperIndex
					, tabIndex: location.state.tabIndex
					, from: location.pathname
				}
			});
		} else {
			navigate(-1);
		}
	}, [navigate, location.state.linkOpenMenu, location.state.query, location.state.swiperIndex, location.state.tabIndex, location.pathname]);
	
	/** 보안키패드 입력요청 (clickSecurityKeypadEvent_Web (WEB > APP)) */
	const clickSecurityKeypadEvent = (type: string, length: number, title: string) => {
		clickSecurityKeypadEventWeb(screenId, screenName, type, length.toString(), title);
	};
	
	/** 보안키패드 입력 (typingSecurityKeypad_App (APP > WEB)) */
	const callbackTypingSecurityKeypadApp = useCallback((event) => {
		if (event.detail.keypadCode === "4") {
			setCvc(Array<string>(Number.parseInt(event.detail.length)).fill("*").join(""));
		} else if (event.detail.keypadCode === "2") {
			setPassword(Array<string>(Number.parseInt(event.detail.length)).fill("*").join(""));
		} else if (event.detail.keypadCode === "5") {
			setPasswordConfirm(Array<string>(Number.parseInt(event.detail.length)).fill("*").join(""));
		}
	}, []);
	
	/** 보안키패드 입력완료 처리 (completedPincode_App (APP > WEB)) */
	const callbackCompletedSecurityKeypadApp = useCallback((event) => {
		let length = event.detail.length === "0" ? (event.detail.keypadCode === "4" ? 3 : 4) : Number.parseInt(event.detail.length);
		
		if (event.detail.keypadCode === "4") {
			setCvc(Array<string>(length).fill("*").join(""));
			cvcRef.current?.classList.remove("error");
			setPasswordChange({
				...passwordChange,
				cvcKeypadPid: event.detail.keypadPid,
				cvcEnteredValue: event.detail.enteredValue
			})
		} else if (event.detail.keypadCode === "2") {
			setPassword(Array<string>(length).fill("*").join(""));
			passwordRef.current?.classList.remove("error");
			setPasswordChange({
				...passwordChange,
				passwordKeypadPid: event.detail.keypadPid,
				passwordEnteredValue: event.detail.enteredValue
			})
			
			client.post("/api/app/manage/password/check", {
				...location.state.query,
				passwordKeypadPid: event.detail.keypadPid,
				passwordEnteredValue: event.detail.enteredValue,
				alternativeCardNo: cardInfo.alternativeCardNo
			}).then((response) => {
					if (response.data.code === "00" || response.data.code === "0000") {
						setPasswordCheckCode(response.data.response.checkResult);
					}
				}
			)
		} else if (event.detail.keypadCode === "5") {
			setPasswordConfirm(Array<string>(length).fill("*").join(""));
			passwordConfirmRef.current?.classList.remove("error");
			setPasswordChange({
				...passwordChange,
				confirmKeypadPid: event.detail.keypadPid,
				confirmEnteredValue: event.detail.enteredValue
			})
		}
	}, [location.state.query, cardInfo.alternativeCardNo, passwordChange]);
	
	/** 비밀번호 변경처리 */
	const clickDone = useCallback(() => {
    showLoading();

    cvcRef.current?.classList.remove('error');
    passwordRef.current?.classList.remove('error');
    passwordConfirmRef.current?.classList.remove('error');

    // 패스워드 변경
    client
      .patch('/api/app/manage/password/change', {
        ...location.state.query,
        alternativeCardNo: cardInfo.alternativeCardNo,
        ...passwordChange
      })
      .then((response) => {
        console.log(response);

        if (response.data.code === '00') {
          setModal(true);
        } else {
          if (response.data.code === '91') {
            cvcRef.current?.classList.add('error');
          } else if (response.data.code === '92') {
            passwordRef.current?.classList.add('error');
          } else if (response.data.code === '93') {
            passwordConfirmRef.current?.classList.add('error');
          } else {
            callAlertWeb('ERROR', response.data.code, response.data.message);
          }
        }
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
      });
  }, [passwordChange, cardInfo, location, showLoading, hideLoading]);
	
	// TODO TEST입력
	const testCall = (type: string) => {
		let keypadPid = "TEST_PID";
		let enteredValue = "TEST_ENTER_VALUE";
		
		if (type === "2") {
			keypadPid = "C1DD17010D5940E587861BDDDDAF544A";
			enteredValue = "c4ae4a276119237d1e0bf4793c7c473cbb20b93bae9b65ebd58f191c5f33e6ecd7cb716efee09270cf61fc08b594bc15a9d9766a4edff52e62f39335e197954e63e16656246a7b5de30457093d885277e601d799";
		} else if (type === "5") {
			keypadPid = "3EDCA9F3EE1A404C8AB6DE78E984AA81";
			enteredValue = "5e59b043e355e71bb7d8a46a26a39dc8ae755d7c017e59276744aa8b09fe76d23b8153f6d004e234a2010d1c0596434ed4755e972727ae34cfc16b07382577c23012de7f9d5a1d7b6d0fe4433b05199832a969e4";
		}
		
		let event = new CustomEvent("completKeypad", {
			detail: {
				keypadPid: keypadPid,
				keypadCode: type,
				enteredValue: enteredValue,
				length: "0"
			}
		});
		document.dispatchEvent(event);
	}
	
	/** 모달 닫기 */
	const closeModal = useCallback(() => {
		setModal(false);
		navigate("/main", {
			state: {
				query: location.state.query,
				swiperIndex: location.state.swiperIndex,
				tabIndex: location.state.tabIndex
			}
		});
	}, [navigate, location]);
	
	// 최초실행
	useEffect(() => {
		// 언어설정
		i18n.changeLanguage(location.state.query.languageCode);
		// callNative_Web 처리 (WEB > APP)
		callNativeWeb(screenId, screenName, location.state.from === "/intro" ? 'Y' : 'N');
		// 하단바 숨기기
		footerShowEventWeb("N");
		
		console.log("QUERY STATE");
		console.log(location.state);
		console.log(cardInfo);
		
		// 최초한번실행 useEffect() Warning 제거
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	// 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("backpress", callbackBackkeyEvent);
		return () => document.removeEventListener("backpress", callbackBackkeyEvent);
	}, [callbackBackkeyEvent]);
	
	// 보안키패드 입력 이벤트 리스너 (typingSecurityKeypad_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("typingKeypad", callbackTypingSecurityKeypadApp);
		return () => document.removeEventListener("typingKeypad", callbackTypingSecurityKeypadApp);
	}, [callbackTypingSecurityKeypadApp]);
	
	// 보안키패드 결과전달 이벤트 리스너 (completedSecurityKeypad_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("completKeypad", callbackCompletedSecurityKeypadApp);
		return () => document.removeEventListener("completKeypad", callbackCompletedSecurityKeypadApp);
	}, [callbackCompletedSecurityKeypadApp]);
	
	return (
		<section className="pdb70">
			<div id="wrap">
				<div id="container" className="pcHidden">
					<section className="contents">
						<div className="singUpWrapSub mgt20">
							<div className="inputBoxTy01">
								<label>
									<input placeholder=" " type="text" value={cardInfo.cardNo} readOnly/>
									<span>{t("SERV02.header01")}</span>
								</label>
							</div>
							<div className="inputBoxTy01">
								<label onClick={() => clickSecurityKeypadEvent("4", 3, t("SERV02.header02"))}>
									<input placeholder=" " type="password" value={cvc} maxLength={3} readOnly
										   className="inputReadOnly"
									/>
									<span>{t("SERV02.header02")}</span>
									<p className="message" ref={cvcRef}
									   onClick={e => e.stopPropagation()}
									>
										{t("SERV02.explan01")}
									</p>
								</label>
							</div>
							<div className="inputBoxTy01">
								<label onClick={() => clickSecurityKeypadEvent("2", 4, t("SERV02.header03"))}>
									<input placeholder=" " type="password" value={password} maxLength={4} readOnly
										   className="inputReadOnly"
									/>
									<span>{t("SERV02.header03")}</span>
									<p className="message" ref={passwordRef}
									   onClick={e => e.stopPropagation()}
									>
										{t("SERV02.explan02")}
									</p>
									<div className={"rounded mgt8"}>
										<div className={'onLine'}>
											{
												(passwordCheckCode !== '') &&
                                                <img className={'circle'} alt=""
                                                     src={passwordCheckCode === '10' || passwordCheckCode === '11' ? '/assets/images/ico/icon-pw-error.png' : '/assets/images/ico/icon_pw_check.png'}
                                                />
											}
											{
												(passwordCheckCode === '') &&
                                                <img className={'circle'} alt=""
                                                     src="/assets/images/ico/icon_pw_check_empty.png"
                                                />
											}
											<p className={'message mgl5'}
											   style={passwordCheckCode !== '' ? {color: "#111111"} : {}}
											>
												{t('SERV02.explan03.01', 'Do not contain consecutive or identical numbers(1111, 1234)')}
											</p>
										</div>
										<div className={'onLine mgb5'}>
											{
												(passwordCheckCode !== '') &&
                                                <img className={'circle'} alt=""
                                                     src={passwordCheckCode === '01' || passwordCheckCode === '11' ? '/assets/images/ico/icon-pw-error.png' : '/assets/images/ico/icon_pw_check.png'}
                                                />
											}
											{
												(passwordCheckCode === '') &&
                                                <img className={'circle'} alt=""
                                                     src="/assets/images/ico/icon_pw_check_empty.png"
                                                />
											}
											<p className={'message mgl5'}
											   style={passwordCheckCode !== '' ? {color: "#111111"} : {}}
											>
												{t('SERV02.explan03.02', 'Do not contain ID Number, Date of Birth, Phone Number, Card Number')}
											</p>
										</div>
									</div>
								</label>
							</div>
							<div className="inputBoxTy01">
								<label onClick={() => clickSecurityKeypadEvent('5', 4, t('SERV02.header04'))}>
									<input placeholder=" " type="password" value={passwordConfirm} maxLength={4}
										   readOnly className="inputReadOnly"
									/>
									<span>{t("SERV02.header04")}</span>
									<p className="message" ref={passwordConfirmRef}
									   onClick={e => e.stopPropagation()}
									>
										{t("SERV02.explan03")}
									</p>
								</label>
							</div>
						</div>
						{process.env.REACT_APP_PROFILE !== "prod" &&
                            <>
                                <button className="btnPrimaryTy01" onClick={() => testCall("4")}>
                                    <span>CVC 입력</span>
                                </button>
                                <button className="btnPrimaryTy01" onClick={() => testCall("2")}>
                                    <span>카드비밀번호 입력</span>
                                </button>
                                <button className="btnPrimaryTy01" onClick={() => testCall("5")}>
                                    <span>카드비밀번호 확인 입력</span>
                                </button>
                            </>
						}
					</section>
					<div className="btnArea">
						<div className="btnWrapTy01">
							<button type="button" className="btnPrimaryTy01"
									disabled={stringUtils.checkValueNull(passwordChange.cvcEnteredValue) || stringUtils.checkValueNull(passwordChange.passwordEnteredValue) || stringUtils.checkValueNull(passwordChange.confirmEnteredValue) || passwordCheckCode !== '00'}
									onClick={clickDone}
							>
								<span>{t("COMMON.button.done")}</span>
							</button>
						</div>
					</div>
				</div>
				<Modal isOpen={isModal} contentLabel="modal" onRequestClose={closeModal} className="popTyLayer2"
					   overlayClassName="dim" closeTimeoutMS={500}
				>
					<p className="message">{t("SERV02.popup02")}</p>
					<dl>
						{/*<dt>{t("SERV02.popup03")}</dt>*/}
						<dd><span>{cardInfo.cardNo}</span></dd>
					</dl>
					<div className="btnWrapTy02">
						<button type="button" className="btnPrimaryTy01" onClick={closeModal}>
							<span>{t("COMMON.button.confirm")}</span></button>
					</div>
				</Modal>
			</div>
		</section>
	);
};

export default PasswordChangePage;